import { cx } from '@linaria/core';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useRef } from 'react';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { Row, Cell, IconButton, RetryIcon } from '@ui/kit';
import { formatTimestamp } from '@utils/common';
import { useMatchBreakpoint, useToggleState } from '@utils/react';
import { deepCompare } from '../../../utils/deep-compare.js';
import { getDialoguesQuery } from '../../dialogues/api.get-dialogues.query.js';
import { DialogOverlay } from '../../dialogues/overlay.dialog.js';
import { getComponentEntriesQuery } from '../api/query.get-component-entries.js';
import { EntriesDialogIcon } from '../atoms/icon.dialog.js';
import { Stats } from '../atoms/stats.js';
import { makePreviewModalConfig } from '../molecules/modal.preview.js';
import { componentEntryStore } from '../state.js';
import { row, actionButtons, mobileContainer, mobileTopSection, mobileLeftSection, mobileHeadingCell, mobileProductCell, mobileDialogCell, mobileRightSection, mobileSharedCell, mobileDateCell, mobileStatsCell } from './table.item.style.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useDeleteNotificationMutation } from '../../notifications/api.delete-notification.mutation.js';
import { rowSizingCls } from './table.root.style.js';

const TableItem = /*#__PURE__*/memo(function TableItem(props) {
  const user = useUserIdentityState(state => state.user);
  const notifier = useNotifier();
  const queryClient = useQueryClient();
  const deleteNotification = useDeleteNotificationMutation();
  const isMobile = useMatchBreakpoint(breakpoints.mobile, {
    immediate: true
  });
  const dialogButtonRef = useRef(null);
  const [showDialog, actions] = useToggleState();
  const onDialogueClick = async evt => {
    evt.stopPropagation();
    if (!props.item.dialogue.id) {
      return;
    }
    await queryClient.prefetchQuery(getDialoguesQuery(user, props.item.dialogue.id));
    for (const dialogueNotification of props.item.dialogue.notifications) {
      deleteNotification.mutate([dialogueNotification]);
    }
    actions.enable();
  };
  const openPreview = async () => {
    if (props.type === "finished") {
      await notifier.modal(makePreviewModalConfig({
        entries: props.entries,
        initialIndex: props.index
      }));
    } else {
      await notifier.modal(makePreviewModalConfig({
        entries: [props.item],
        initialIndex: 0,
        disableHeaderCenterPanel: true
      }));
    }

    // Ensure we refetch all component-entries after closing the retried entry
    // as this will result in the newly created entry being added to the table
    // instantly.
    queryClient.refetchQueries(getComponentEntriesQuery(user, componentEntryStore.getState().filters.timespan));
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Row, {
      className: cx(row, props.type),
      sizingCls: rowSizingCls,
      onClick: openPreview,
      children: isMobile ? /*#__PURE__*/jsx(MobileRowContent, {
        ...props,
        dialogButtonRef: dialogButtonRef,
        onDialogueClick: onDialogueClick
      }) : /*#__PURE__*/jsx(DesktopRowContent, {
        ...props,
        dialogButtonRef: dialogButtonRef,
        onDialogueClick: onDialogueClick
      })
    }), props.type === "finished" && /*#__PURE__*/jsx(DialogOverlay, {
      active: showDialog,
      pointAt: dialogButtonRef,
      dialogId: props.item.dialogue.id,
      componentTitle: props.item.component.title,
      onClose: actions.disable
    })]
  });
}, deepCompare);
function DesktopRowContent(props) {
  const notifier = useNotifier();
  const queryClient = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const retry = async evt => {
    evt.stopPropagation();
    await notifier.modal(makePreviewModalConfig({
      entries: [props.item],
      initialIndex: 0,
      disableHeaderCenterPanel: true,
      startFromScratch: true
    }));

    // Ensure we refetch all component-entries after closing the retried entry
    // as this will result in the newly created entry being added to the table
    // instantly.
    queryClient.refetchQueries(getComponentEntriesQuery(user, componentEntryStore.getState().filters.timespan));
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Cell, {
      role: "rowheader",
      children: props.item.component.title
    }), /*#__PURE__*/jsx(Cell, {
      children: props.item.product?.title
    }), /*#__PURE__*/jsx(Cell, {
      children: formatTimestamp(props.item.modifiedAt ?? props.item.createdAt)
    }), props.type === "finished" && /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Cell, {
        children: props.item.status.isShared ? "Ja" : "Nej"
      }), /*#__PURE__*/jsx(Cell, {
        children: /*#__PURE__*/jsx(Stats, {
          score: props.item.status.score
        })
      }), /*#__PURE__*/jsx(Cell, {
        children: /*#__PURE__*/jsxs("div", {
          className: actionButtons,
          children: [/*#__PURE__*/jsx(IconButton, {
            icon: /*#__PURE__*/jsx(RetryIcon, {
              height: "20"
            }),
            label: "L\xF8s opgave igen",
            onClick: retry
          }), props.item.dialogue.id && /*#__PURE__*/jsx(IconButton, {
            ref: props.dialogButtonRef,
            icon: /*#__PURE__*/jsx(EntriesDialogIcon, {
              hasNewNotifications: props.item.dialogue.notifications.length > 0,
              height: "19"
            }),
            label: "\xC5bn dialog",
            async: {
              showThrobberWhilePending: true
            },
            onClick: props.onDialogueClick
          })]
        })
      })]
    })]
  });
}
function MobileRowContent(props) {
  return /*#__PURE__*/jsxs("div", {
    className: mobileContainer,
    children: [/*#__PURE__*/jsxs("div", {
      className: mobileTopSection,
      children: [/*#__PURE__*/jsxs("div", {
        className: mobileLeftSection,
        children: [/*#__PURE__*/jsx(Cell, {
          role: "rowheader",
          className: mobileHeadingCell,
          children: props.item.component.title
        }), /*#__PURE__*/jsx(Cell, {
          className: mobileProductCell,
          children: props.item.product?.title
        })]
      }), props.type === "finished" && /*#__PURE__*/jsx(Cell, {
        className: mobileDialogCell,
        children: /*#__PURE__*/jsx(IconButton, {
          ref: props.dialogButtonRef,
          icon: /*#__PURE__*/jsx(EntriesDialogIcon, {
            hasNewNotifications: props.item.dialogue.notifications.length > 0,
            height: "16"
          }),
          label: "\xC5bn dialog",
          async: {
            showThrobberWhilePending: true
          },
          onClick: props.onDialogueClick
        })
      }), /*#__PURE__*/jsxs("div", {
        className: cx(mobileRightSection, props.type),
        children: [props.type === "finished" && /*#__PURE__*/jsx(Cell, {
          className: mobileSharedCell,
          children: props.item.status.isShared ? "Delt" : "Ikke delt"
        }), /*#__PURE__*/jsx(Cell, {
          className: mobileDateCell,
          children: formatTimestamp(props.item.modifiedAt ?? props.item.createdAt)
        })]
      })]
    }), props.type === "finished" && /*#__PURE__*/jsx(Cell, {
      className: mobileStatsCell,
      children: /*#__PURE__*/jsx(Stats, {
        score: props.item.status.score
      })
    })]
  });
}

export { TableItem };
//# sourceMappingURL=table.item.js.map
